import type { DyeColorId, BannerPatternId } from './types';
import { isVersionAtLeast } from '@/app/lib/versions';

// ── Dye Color Registry ─────────────────────────────────────────────────

export interface DyeColorInfo {
  id: DyeColorId;
  displayName: string;
  hex: string;
  rgb: [number, number, number];
  legacyId: number;
}

// 🚨 APPEND-ONLY. ORDER IS DATA — see the note on BANNER_PATTERNS below.
// The share hash stores `DYE_COLORS.findIndex(...)` for the base color and every layer color
// (exporter.ts:553, :558). Reordering silently recolors every existing banner.
export const DYE_COLORS: DyeColorInfo[] = [
  { id: 'white',      displayName: 'White',      hex: '#F9FFFE', rgb: [249, 255, 254], legacyId: 0 },
  { id: 'orange',     displayName: 'Orange',     hex: '#F9801D', rgb: [249, 128, 29],  legacyId: 1 },
  { id: 'magenta',    displayName: 'Magenta',    hex: '#C74EBD', rgb: [199, 78, 189],  legacyId: 2 },
  { id: 'light_blue', displayName: 'Light Blue', hex: '#3AB3DA', rgb: [58, 179, 218],  legacyId: 3 },
  { id: 'yellow',     displayName: 'Yellow',     hex: '#FED83D', rgb: [254, 216, 61],  legacyId: 4 },
  { id: 'lime',       displayName: 'Lime',       hex: '#80C71F', rgb: [128, 199, 31],  legacyId: 5 },
  { id: 'pink',       displayName: 'Pink',       hex: '#F38BAA', rgb: [243, 139, 170], legacyId: 6 },
  { id: 'gray',       displayName: 'Gray',       hex: '#474F52', rgb: [71, 79, 82],    legacyId: 7 },
  { id: 'light_gray', displayName: 'Light Gray', hex: '#9D9D97', rgb: [157, 157, 151], legacyId: 8 },
  { id: 'cyan',       displayName: 'Cyan',       hex: '#169C9C', rgb: [22, 156, 156],  legacyId: 9 },
  { id: 'purple',     displayName: 'Purple',     hex: '#8932B8', rgb: [137, 50, 184],  legacyId: 10 },
  { id: 'blue',       displayName: 'Blue',       hex: '#3C44AA', rgb: [60, 68, 170],   legacyId: 11 },
  { id: 'brown',      displayName: 'Brown',      hex: '#835432', rgb: [131, 84, 50],   legacyId: 12 },
  { id: 'green',      displayName: 'Green',      hex: '#5E7C16', rgb: [94, 124, 22],   legacyId: 13 },
  { id: 'red',        displayName: 'Red',        hex: '#B02E26', rgb: [176, 46, 38],   legacyId: 14 },
  { id: 'black',      displayName: 'Black',      hex: '#1D1D21', rgb: [29, 29, 33],    legacyId: 15 },
];

export const DYE_COLOR_MAP = new Map(DYE_COLORS.map(c => [c.id, c]));

// ── Banner Pattern Registry ────────────────────────────────────────────

export type PatternCategory = 'Stripes' | 'Crosses' | 'Halves' | 'Corners' | 'Triangles' | 'Diagonals' | 'Shapes' | 'Gradients' | 'Special';

export interface BannerPatternInfo {
  id: BannerPatternId;
  displayName: string;
  legacyCode: string;
  maskFile: string;
  requiresItem: boolean;
  category: PatternCategory;
  minVersion?: string;
}

// 🚨 APPEND-ONLY. ORDER IS DATA.
// `encodeToHash` stores `BANNER_PATTERNS.findIndex(...)` in the share hash (exporter.ts:557) and
// `decodeFromHash` reads it back positionally — the hash carries no format version. So reordering
// this array, including the purely cosmetic act of regrouping it by the `// Stripes` / `// Crosses`
// comments below, SILENTLY REWRITES every share link and every saved banner into a different
// design. Nothing errors, nothing logs, and the preview is happy to draw the wrong thing.
// Add new patterns at the END, and never remove or reorder an existing entry.
// (The same contract is stated at app/lib/commands.ts:1214 for the same reason.)
export const BANNER_PATTERNS: BannerPatternInfo[] = [
  // Stripes
  { id: 'minecraft:stripe_top',       displayName: 'Top Stripe',         legacyCode: 'ts',  maskFile: 'stripe_top.png',       requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_bottom',    displayName: 'Bottom Stripe',      legacyCode: 'bs',  maskFile: 'stripe_bottom.png',    requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_left',      displayName: 'Left Stripe',        legacyCode: 'ls',  maskFile: 'stripe_left.png',      requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_right',     displayName: 'Right Stripe',       legacyCode: 'rs',  maskFile: 'stripe_right.png',     requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_center',    displayName: 'Center Stripe',      legacyCode: 'cs',  maskFile: 'stripe_center.png',    requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_middle',    displayName: 'Middle Stripe',      legacyCode: 'ms',  maskFile: 'stripe_middle.png',    requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_downright', displayName: 'Down Right Stripe',  legacyCode: 'drs', maskFile: 'stripe_downright.png', requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:stripe_downleft',  displayName: 'Down Left Stripe',   legacyCode: 'dls', maskFile: 'stripe_downleft.png',  requiresItem: false, category: 'Stripes' },
  { id: 'minecraft:small_stripes',    displayName: 'Small Stripes',      legacyCode: 'ss',  maskFile: 'small_stripes.png',    requiresItem: false, category: 'Stripes' },

  // Crosses
  { id: 'minecraft:cross',            displayName: 'Saltire',            legacyCode: 'cr',  maskFile: 'cross.png',            requiresItem: false, category: 'Crosses' },
  { id: 'minecraft:straight_cross',   displayName: 'Cross',              legacyCode: 'sc',  maskFile: 'straight_cross.png',   requiresItem: false, category: 'Crosses' },

  // Diagonals
  { id: 'minecraft:diagonal_left',    displayName: 'Top Left Triangle',  legacyCode: 'ld',  maskFile: 'diagonal_left.png',    requiresItem: false, category: 'Diagonals' },
  { id: 'minecraft:diagonal_right',   displayName: 'Top Right Triangle', legacyCode: 'rd',  maskFile: 'diagonal_right.png',   requiresItem: false, category: 'Diagonals' },
  { id: 'minecraft:diagonal_up_left', displayName: 'Bottom Left Triangle', legacyCode: 'lud', maskFile: 'diagonal_up_left.png', requiresItem: false, category: 'Diagonals' },
  { id: 'minecraft:diagonal_up_right',displayName: 'Bottom Right Triangle',legacyCode: 'rud', maskFile: 'diagonal_up_right.png',requiresItem: false, category: 'Diagonals' },

  // Halves
  { id: 'minecraft:half_vertical',       displayName: 'Left Half',        legacyCode: 'vh',  maskFile: 'half_vertical.png',       requiresItem: false, category: 'Halves' },
  { id: 'minecraft:half_vertical_right', displayName: 'Right Half',       legacyCode: 'vhr', maskFile: 'half_vertical_right.png', requiresItem: false, category: 'Halves' },
  { id: 'minecraft:half_horizontal',     displayName: 'Top Half',         legacyCode: 'hh',  maskFile: 'half_horizontal.png',     requiresItem: false, category: 'Halves' },
  { id: 'minecraft:half_horizontal_bottom', displayName: 'Bottom Half',   legacyCode: 'hhb', maskFile: 'half_horizontal_bottom.png', requiresItem: false, category: 'Halves' },

  // Corners
  { id: 'minecraft:square_top_left',     displayName: 'Top Left Corner',     legacyCode: 'tl', maskFile: 'square_top_left.png',     requiresItem: false, category: 'Corners' },
  { id: 'minecraft:square_top_right',    displayName: 'Top Right Corner',    legacyCode: 'tr', maskFile: 'square_top_right.png',    requiresItem: false, category: 'Corners' },
  { id: 'minecraft:square_bottom_left',  displayName: 'Bottom Left Corner',  legacyCode: 'bl', maskFile: 'square_bottom_left.png',  requiresItem: false, category: 'Corners' },
  { id: 'minecraft:square_bottom_right', displayName: 'Bottom Right Corner', legacyCode: 'br', maskFile: 'square_bottom_right.png', requiresItem: false, category: 'Corners' },

  // Triangles
  { id: 'minecraft:triangle_top',     displayName: 'Top Triangle',       legacyCode: 'tt',  maskFile: 'triangle_top.png',     requiresItem: false, category: 'Triangles' },
  { id: 'minecraft:triangle_bottom',  displayName: 'Bottom Triangle',    legacyCode: 'bt',  maskFile: 'triangle_bottom.png',  requiresItem: false, category: 'Triangles' },
  { id: 'minecraft:triangles_top',    displayName: 'Top Sawtooth',       legacyCode: 'tts', maskFile: 'triangles_top.png',    requiresItem: false, category: 'Triangles' },
  { id: 'minecraft:triangles_bottom', displayName: 'Bottom Sawtooth',    legacyCode: 'bts', maskFile: 'triangles_bottom.png', requiresItem: false, category: 'Triangles' },

  // Shapes
  { id: 'minecraft:circle',  displayName: 'Circle',  legacyCode: 'mc', maskFile: 'circle.png',  requiresItem: false, category: 'Shapes' },
  { id: 'minecraft:rhombus', displayName: 'Rhombus', legacyCode: 'mr', maskFile: 'rhombus.png', requiresItem: false, category: 'Shapes' },
  { id: 'minecraft:border',  displayName: 'Border',  legacyCode: 'bo', maskFile: 'border.png',  requiresItem: false, category: 'Shapes' },
  { id: 'minecraft:bricks',  displayName: 'Bricks',  legacyCode: 'bri', maskFile: 'bricks.png', requiresItem: false, category: 'Shapes' },

  // Gradients
  { id: 'minecraft:gradient',    displayName: 'Gradient Down', legacyCode: 'gra', maskFile: 'gradient.png',    requiresItem: false, category: 'Gradients' },
  { id: 'minecraft:gradient_up', displayName: 'Gradient Up',   legacyCode: 'gru', maskFile: 'gradient_up.png', requiresItem: false, category: 'Gradients' },

  // Special (require banner pattern items)
  { id: 'minecraft:curly_border', displayName: 'Curly Border',    legacyCode: 'cbo', maskFile: 'curly_border.png', requiresItem: true,  category: 'Special' },
  { id: 'minecraft:creeper',      displayName: 'Creeper Charge',  legacyCode: 'cre', maskFile: 'creeper.png',      requiresItem: true,  category: 'Special' },
  { id: 'minecraft:skull',        displayName: 'Skull Charge',    legacyCode: 'sku', maskFile: 'skull.png',        requiresItem: true,  category: 'Special' },
  { id: 'minecraft:flower',       displayName: 'Flower Charge',   legacyCode: 'flo', maskFile: 'flower.png',       requiresItem: true,  category: 'Special' },
  { id: 'minecraft:mojang',       displayName: 'Thing',           legacyCode: 'moj', maskFile: 'mojang.png',       requiresItem: true,  category: 'Special' },
  { id: 'minecraft:globe',        displayName: 'Globe',           legacyCode: 'glb', maskFile: 'globe.png',        requiresItem: true,  category: 'Special' },
  { id: 'minecraft:piglin',       displayName: 'Snout',           legacyCode: 'pig', maskFile: 'piglin.png',       requiresItem: true,  category: 'Special' },
  { id: 'minecraft:flow',         displayName: 'Flow',            legacyCode: 'flw', maskFile: 'flow.png',         requiresItem: true,  category: 'Special', minVersion: '1.21' },
  { id: 'minecraft:guster',       displayName: 'Guster',          legacyCode: 'gus', maskFile: 'guster.png',       requiresItem: true,  category: 'Special', minVersion: '1.21' },
];

export const BANNER_PATTERN_MAP = new Map(BANNER_PATTERNS.map(p => [p.id, p]));

// Patterns grouped by category for the UI
export const PATTERN_CATEGORIES: PatternCategory[] = [
  'Stripes', 'Crosses', 'Diagonals', 'Halves', 'Corners', 'Triangles', 'Shapes', 'Gradients', 'Special',
];

/**
 * Patterns whose loom recipe changed: before 1.21.2 the loom made Bricks and Curly Border with no
 * item (both were in `#minecraft:no_item_required`); from 1.21.2 they need the Field Masoned and
 * Bordure Indented banner patterns, items that did not exist before. Read from the vanilla
 * `banner_pattern` tags of 1.20.4, 1.21.4 and 26.3.
 */
const ITEM_REQUIRED_FROM: Partial<Record<string, string>> = {
  'minecraft:bricks': '1.21.2',
  'minecraft:curly_border': '1.21.2',
};

/** Whether the loom needs a banner pattern item for this pattern on `version`. */
export function patternRequiresItem(pattern: BannerPatternInfo, version: string): boolean {
  const from = ITEM_REQUIRED_FROM[pattern.id];
  return from ? isVersionAtLeast(version, from) : pattern.requiresItem;
}

export function getAvailablePatterns(version: string): BannerPatternInfo[] {
  return BANNER_PATTERNS.filter(p => !p.minVersion || isVersionAtLeast(version, p.minVersion));
}

export function getPatternsByCategory(version?: string): Map<PatternCategory, BannerPatternInfo[]> {
  const patterns = version ? getAvailablePatterns(version) : BANNER_PATTERNS;
  const map = new Map<PatternCategory, BannerPatternInfo[]>();
  for (const cat of PATTERN_CATEGORIES) {
    const catPatterns = patterns.filter(p => p.category === cat);
    if (catPatterns.length > 0) map.set(cat, catPatterns);
  }
  return map;
}

// ── Rendering Constants ────────────────────────────────────────────────

export const BANNER_MASK_BASE = '/minecraft-assets/textures/entity/banner/';
export const SHIELD_MASK_BASE = '/minecraft-assets/textures/entity/shield/';
export const SHIELD_BASE_TEXTURE = '/minecraft-assets/textures/entity/shield_base.png';

// UV rectangle for banner front face within 64x64 mask textures
export const BANNER_UV = { x: 1, y: 1, w: 20, h: 40 };

// UV rectangle for shield front face within 64x64 mask textures
export const SHIELD_UV = { x: 2, y: 2, w: 10, h: 20 };

// The full front-face section of the shield texture (pattern area + border)
export const SHIELD_FRONT = { x: 0, y: 0, w: 14, h: 23 };

// Mask texture dimensions
export const MASK_TEXTURE_SIZE = 64;

// Canvas display size
export const CANVAS_WIDTH = 280;
export const CANVAS_HEIGHT = 400;

// Layer limits
export const MAX_LAYERS_SURVIVAL = 6;
export const MAX_LAYERS_CREATIVE = 16;
